import { makeStyles } from '@material-ui/core/styles';
import { MOBILE_TAB_BAR_HEIGHT } from '../../nav/MobileTabBar';
import { ChevronDownIcon, DocumentFilledIcon } from '../../../components/icons';
import type { DocsNavPillProps } from './types';

/*
 * The phone reader's navigation trigger (mock 3c): a split charcoal pill
 * floating above the bottom tab bar — [ Contents | On this page · 2/6 ⌄ ].
 *
 * Both segments open the same sheet, on their own tab. The pill is
 * presentational and stays charcoal on either theme, as the mock's does; the
 * surface mounting it owns the state and the data.
 */
const useStyles = makeStyles({
  // The tab bar's height plus the mock's 12px gap; the shell adds the safe-area
  // inset under the bar, so the pill clears that too.
  root: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 12px)`,
    // Under the tab bar (1400) and over the reader; the sheet goes above both.
    zIndex: 1300,
    display: 'flex',
    justifyContent: 'center',
    // Only the pill takes taps; the strip it sits in must not swallow scrolls
    // over the prose beside it.
    pointerEvents: 'none',
  },
  pill: {
    pointerEvents: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    height: 44,
    borderRadius: 999,
    background: 'var(--hone-portal-surface-solid)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
    // The segments' own corners would show through the pill's radius.
    overflow: 'hidden',
  },
  segment: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 7,
    border: 'none',
    background: 'none',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12.5,
    fontWeight: 500,
    color: '#fefeec',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  // The two halves are padded tighter against the divider than the outer edges.
  contentsSegment: {
    padding: '0 14px 0 18px',
  },
  tocSegment: {
    padding: '0 18px 0 14px',
  },
  // A pill of one segment has no divider to lean on and takes even padding.
  soleSegment: {
    padding: '0 18px',
  },
  divider: {
    width: 1,
    alignSelf: 'stretch',
    background: 'rgba(255, 255, 255, 0.16)',
  },
  // Inline glyphs inherit the segment's near-white ink via currentColor, so the
  // pill stays charcoal-correct on either theme without a filter.
  documentIcon: {
    width: 13,
    height: 13,
    opacity: 0.7,
  },
  chevronIcon: {
    width: 12,
    height: 12,
    opacity: 0.7,
  },
});

export const DocsNavPill = ({
  onThisPageLabel,
  hasContents,
  onOpenContents,
  onOpenToc,
}: DocsNavPillProps) => {
  const classes = useStyles();

  if (!hasContents && !onThisPageLabel) return null;

  const split = hasContents && Boolean(onThisPageLabel);

  return (
    <div className={classes.root}>
      <div className={classes.pill}>
        {hasContents && (
          <button
            type="button"
            className={`${classes.segment} ${
              split ? classes.contentsSegment : classes.soleSegment
            }`}
            onClick={onOpenContents}
          >
            <DocumentFilledIcon className={classes.documentIcon} />
            Contents
          </button>
        )}
        {split && <span className={classes.divider} />}
        {onThisPageLabel && (
          <button
            type="button"
            className={`${classes.segment} ${
              split ? classes.tocSegment : classes.soleSegment
            }`}
            onClick={onOpenToc}
          >
            {onThisPageLabel}
            <ChevronDownIcon className={classes.chevronIcon} />
          </button>
        )}
      </div>
    </div>
  );
};
